export type ThemeTransitionOrigin = { x: number; y: number };

export const THEME_TRANSITION_MS = 1280;
const APPLY_AT_MS = 520;
const FADE_OUT_MS = 980;

const LIGHT_SURFACE = '#fafafa';
const DARK_SURFACE = '#09090b';

let transitionLock = false;

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  hue: number;
  sat: number;
  light: number;
  drag: number;
  twinkle: number;
};

function revealRadius(x: number, y: number): number {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return Math.ceil(Math.hypot(Math.max(x, w - x), Math.max(y, h - y))) + 24;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function setOriginVars(origin: ThemeTransitionOrigin, radius: number): void {
  const root = document.documentElement;
  root.style.setProperty('--theme-ox', `${origin.x}px`);
  root.style.setProperty('--theme-oy', `${origin.y}px`);
  root.style.setProperty('--theme-or', `${radius}px`);
  root.style.setProperty('--theme-blob-scale', String((radius * 2.4) / 48));
  root.style.setProperty('--theme-ring-scale', String(radius / 14));
}

function clearOriginVars(): void {
  const root = document.documentElement;
  root.style.removeProperty('--theme-ox');
  root.style.removeProperty('--theme-oy');
  root.style.removeProperty('--theme-or');
  root.style.removeProperty('--theme-blob-scale');
  root.style.removeProperty('--theme-ring-scale');
}

function spawnParticles(origin: ThemeTransitionOrigin, toDark: boolean): Particle[] {
  const count = toDark ? 140 : 110;
  const out: Particle[] = [];

  for (let i = 0; i < count; i += 1) {
    const angle = Math.random() * Math.PI * 2;
    const speed = toDark ? 3 + Math.random() * 11 : 4 + Math.random() * 14;
    const spread = toDark ? 1 : 0.85 + Math.random() * 0.3;

    out.push({
      x: origin.x + (Math.random() - 0.5) * 12,
      y: origin.y + (Math.random() - 0.5) * 12,
      vx: Math.cos(angle) * speed * spread,
      vy: Math.sin(angle) * speed * spread,
      life: 0,
      maxLife: 38 + Math.random() * 52,
      size: toDark ? 0.8 + Math.random() * 2.8 : 1.2 + Math.random() * 4.5,
      hue: toDark ? 210 + Math.random() * 80 : 32 + Math.random() * 28,
      sat: toDark ? 55 + Math.random() * 35 : 88 + Math.random() * 12,
      light: toDark ? 68 + Math.random() * 28 : 52 + Math.random() * 22,
      drag: toDark ? 0.965 + Math.random() * 0.02 : 0.958 + Math.random() * 0.025,
      twinkle: Math.random() * Math.PI * 2,
    });
  }

  return out;
}

function drawParticles(
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  toDark: boolean,
  w: number,
  h: number
): void {
  ctx.clearRect(0, 0, w, h);

  for (const p of particles) {
    if (p.life >= p.maxLife) continue;

    const t = p.life / p.maxLife;
    const fade = toDark ? 1 - t * t : 1 - t * t * t;
    const tw = toDark ? 0.55 + Math.sin(p.life * 0.35 + p.twinkle) * 0.45 : 1;
    const alpha = Math.max(0, fade * tw);

    ctx.beginPath();
    ctx.fillStyle = `hsla(${p.hue}, ${p.sat}%, ${p.light}%, ${alpha})`;
    ctx.arc(p.x, p.y, p.size * (1 - t * 0.35), 0, Math.PI * 2);
    ctx.fill();

    if (!toDark && t < 0.35) {
      ctx.beginPath();
      ctx.strokeStyle = `hsla(${p.hue}, 100%, 78%, ${alpha * 0.45})`;
      ctx.lineWidth = p.size * 0.35;
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(p.x - p.vx * 2.2, p.y - p.vy * 2.2);
      ctx.stroke();
    }
  }
}

function runParticleCanvas(
  canvas: HTMLCanvasElement,
  origin: ThemeTransitionOrigin,
  toDark: boolean,
  durationMs: number
): () => void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const resize = () => {
    canvas.width = Math.floor(window.innerWidth * dpr);
    canvas.height = Math.floor(window.innerHeight * dpr);
    canvas.style.width = `${window.innerWidth}px`;
    canvas.style.height = `${window.innerHeight}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  };

  resize();
  window.addEventListener('resize', resize);

  const particles = spawnParticles(origin, toDark);
  const started = performance.now();
  let alive = true;
  let rafId = 0;

  const tick = (now: number) => {
    if (!alive) return;
    const elapsed = now - started;

    for (const p of particles) {
      if (p.life >= p.maxLife) continue;
      p.life += 1;
      p.x += p.vx;
      p.y += p.vy;
      p.vx *= p.drag;
      p.vy *= p.drag;
      if (toDark) p.vy -= 0.015;
      else p.vy += 0.025;
    }

    drawParticles(ctx, particles, toDark, window.innerWidth, window.innerHeight);

    if (elapsed < durationMs) {
      rafId = requestAnimationFrame(tick);
    }
  };

  rafId = requestAnimationFrame(tick);

  return () => {
    alive = false;
    cancelAnimationFrame(rafId);
    window.removeEventListener('resize', resize);
  };
}

function buildStage(toDark: boolean, origin: ThemeTransitionOrigin): {
  root: HTMLDivElement;
  stopParticles: () => void;
} {
  const root = document.createElement('div');
  root.className = `theme-cinematic ${toDark ? 'theme-cinematic--dark' : 'theme-cinematic--light'}`;
  root.setAttribute('aria-hidden', 'true');

  const canvas = document.createElement('canvas');
  canvas.className = 'theme-cinematic-canvas';

  const rings = document.createElement('div');
  rings.className = 'theme-cinematic-rings';
  rings.innerHTML = '<span></span><span></span><span></span><span></span>';

  const sweep = document.createElement('div');
  sweep.className = 'theme-cinematic-sweep';

  const flash = document.createElement('div');
  flash.className = 'theme-cinematic-flash';

  const blob = document.createElement('div');
  blob.className = 'theme-cinematic-blob';
  blob.style.background = toDark
    ? `radial-gradient(circle at 30% 30%, #1e293b 0%, ${DARK_SURFACE} 55%, #020617 100%)`
    : `radial-gradient(circle at 35% 35%, #fffbeb 0%, ${LIGHT_SURFACE} 50%, #e4e4e7 100%)`;

  const mesh = document.createElement('div');
  mesh.className = 'theme-cinematic-mesh';

  root.append(canvas, mesh, sweep, rings, blob, flash);
  document.body.appendChild(root);

  requestAnimationFrame(() => {
    root.classList.add('is-active');
  });

  const stopParticles = runParticleCanvas(canvas, origin, toDark, THEME_TRANSITION_MS);

  return { root, stopParticles };
}

function runCinematicTransition(toDark: boolean, origin: ThemeTransitionOrigin, apply: () => void): void {
  const radius = revealRadius(origin.x, origin.y);
  setOriginVars(origin, radius);

  const html = document.documentElement;
  html.classList.add('theme-cinematic-active');

  const { root, stopParticles } = buildStage(toDark, origin);

  window.setTimeout(() => {
    apply();
    html.classList.add('theme-cinematic-settled');
  }, APPLY_AT_MS);

  window.setTimeout(() => {
    root.classList.add('is-exiting');
  }, FADE_OUT_MS);

  window.setTimeout(() => {
    stopParticles();
    root.remove();
    html.classList.remove('theme-cinematic-active', 'theme-cinematic-settled');
    clearOriginVars();
  }, THEME_TRANSITION_MS);
}

/** Cinematic theme shift — particles, shockwaves, blur, and liquid reveal from the toggle. */
export function playThemeTransition(
  toDark: boolean,
  origin: ThemeTransitionOrigin,
  apply: () => void
): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    apply();
    return;
  }

  if (prefersReducedMotion()) {
    apply();
    return;
  }

  if (transitionLock) {
    apply();
    return;
  }

  transitionLock = true;
  window.setTimeout(() => {
    transitionLock = false;
  }, THEME_TRANSITION_MS + 100);

  runCinematicTransition(toDark, origin, apply);
}
